import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { ROTATE_MS, prefersReducedMotion, useRotation } from "@/components/app/featured-cities/useRotation";
import { MatchBadge } from "@/components/app/featured-cities/MatchBadge";
import { ShareMenu } from "@/components/app/featured-cities/ShareCity";
import { slugTestId } from "@/components/app/featured-cities/cityMeta";
import { subjectSlug } from "@/lib/rankingSlug";
import { CitiesTile, CoursesTile, PayByCountry, RankingTile, SubjectCta, SubjectHero, SubjectKpis, subjectMeta } from "./SubjectBlocks";

function Tabs({ items, index, go, paused, cycle }) {
  const ref = useRef(null);
  useEffect(() => {
    const tab = ref.current?.children[index];
    if (tab) ref.current.scrollLeft = tab.offsetLeft - ref.current.clientWidth / 2 + tab.clientWidth / 2;
  }, [index]);
  return (
    <div className="flex items-center gap-2 sm:gap-3 pb-4 mb-6 border-b border-stone-200" data-testid="featured-subjects-nav-bar">
      <div ref={ref} className="relative flex gap-1 sm:gap-2 overflow-x-auto no-scrollbar min-w-0 flex-1 scroll-smooth" role="tablist">
        {items.map((s, i) => (
          <button key={s.subject} role="tab" aria-selected={i === index} onClick={() => go(i)}
            className={`relative shrink-0 px-3 py-2 text-sm font-medium rounded-md transition-colors ${i === index ? "text-stone-900 bg-white border border-stone-200" : "text-stone-500 hover:text-stone-900"}`}
            data-testid={`featured-subjects-tab-${slugTestId(s.subject)}`}>
            {s.subject}
            {i === index && (
              <span className="absolute left-2 right-2 bottom-0.5 h-0.5 bg-stone-200 overflow-hidden rounded">
                <span key={cycle} className="block h-full featured-city-progress" style={{ background: subjectMeta(s.subject).accent, animationDuration: `${ROTATE_MS}ms`, animationPlayState: paused ? "paused" : "running" }} />
              </span>
            )}
          </button>
        ))}
      </div>
      <button onClick={() => go(index - 1)} aria-label="Previous subject" className="p-2 rounded-full border border-stone-300 hover:bg-white transition-colors" data-testid="featured-subjects-prev-button"><ChevronLeft className="h-4 w-4" /></button>
      <button onClick={() => go(index + 1)} aria-label="Next subject" className="p-2 rounded-full border border-stone-300 hover:bg-white transition-colors" data-testid="featured-subjects-next-button"><ChevronRight className="h-4 w-4" /></button>
    </div>
  );
}

function Spread({ s, match }) {
  const { formatFrom } = useCurrency();
  const m = subjectMeta(s.subject);
  const slug = subjectSlug(s.subject);
  const mine = match?.subjects?.[s.subject];
  const badge = <MatchBadge match={mine} prefs={[]} title={`How ${s.subject} fits you`} withLifestyle={false} tid="featured-subjects" />;
  const card = {
    tid: "featured-subjects", accent: m.accent, photo: m.photo, kicker: "Featured subject", title: s.subject,
    subtitle: `${s.courses.toLocaleString()} courses · ${s.universities.toLocaleString()} universities · ${s.countries} countries`,
    stats: [[s.median_pay_usd ? formatFrom(s.median_pay_usd, "USD") : "—", "Median grad pay"], [s.pay_p90_usd ? formatFrom(s.pay_p90_usd, "USD") : "—", "Top 10% of courses"],
      [s.employment_rate ? `${Math.round(s.employment_rate * 100)}%` : "—", "In work or study"], [s.entry.common[0] || "—", "Typical A-levels"]],
    chips: (s.rankings[0]?.rows || []).slice(0, 3).map((r) => `#${r.rank} ${r.name.replace(/^University of /, "")}`),
    quote: null,
    match: mine?.score != null ? {
      score: mine.score,
      reasons: [...mine.parts].sort((a, b) => b.score - a.score).slice(0, 2).map((p) => `${p.label}: ${p.reason}`),
    } : null,
  };
  const share = <ShareMenu name={`subject-${slug}`} title={s.subject} card={card} tid="featured-subjects" label="Share this subject"
    url={`${window.location.origin}/?subject=${slug}`} text={`${s.subject}: ${s.courses.toLocaleString()} courses, typical graduate pay ${card.stats[0][0]}. See the ranking on Which University Advisor`} />;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
      <SubjectHero s={s} badge={badge} />
      <SubjectKpis s={s} />
      <RankingTile s={s} accent={m.accent} />
      <PayByCountry s={s} accent={m.accent} />
      <CitiesTile s={s} accent={m.accent} />
      <CoursesTile s={s} accent={m.accent} />
      <SubjectCta s={s} share={share} />
    </div>
  );
}

export default function FeaturedSubjectsPromo() {
  const [items, setItems] = useState([]);
  const [match, setMatch] = useState(null);
  const [params] = useSearchParams();
  const { user } = useAuth();
  const ref = useRef(null);
  const deep = params.get("subject");
  const initial = useMemo(() => Math.max(0, items.findIndex((s) => subjectSlug(s.subject) === deep)), [items, deep]);
  const { index, paused, setPaused, go, cycle } = useRotation(items.length, initial);
  const reduced = prefersReducedMotion();

  useEffect(() => { api.get("/subjects/featured").then(({ data }) => setItems(data.subjects || [])).catch(() => setItems([])); }, []);
  const loadMatch = useCallback(() => {
    if (!user) { setMatch(null); return; }
    api.get("/subjects/featured/match").then(({ data }) => setMatch(data)).catch(() => setMatch(null));
  }, [user]);
  useEffect(() => { loadMatch(); }, [loadMatch]);
  useEffect(() => { if (deep && items.length && ref.current) ref.current.scrollIntoView({ behavior: reduced ? "auto" : "smooth" }); }, [deep, items.length, reduced]);

  if (!items.length) return null;
  const s = items[index];
  return (
    <section ref={ref} id="featured-subjects" className="border-y border-stone-200 bg-white overflow-x-hidden scroll-mt-40" data-testid="featured-subjects-promo-section"
      tabIndex={0} aria-roledescription="carousel" aria-label="Featured subjects"
      onKeyDown={(e) => { if (e.key === "ArrowRight") go(index + 1); if (e.key === "ArrowLeft") go(index - 1); }}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false); }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 mb-6">
          <div>
            <p className="font-data text-xs uppercase tracking-widest text-stone-500">Featured subject</p>
            <h2 className="font-editorial text-3xl sm:text-4xl text-stone-900">What a subject is really worth</h2>
          </div>
          <p className="text-sm text-stone-500 sm:text-right max-w-sm">Rankings, graduate pay, cities and employers, all from our course and outcomes data.</p>
        </div>
        <Tabs items={items} index={index} go={go} paused={paused} cycle={cycle} />
        <AnimatePresence mode="wait">
          <motion.div key={s.subject} initial={reduced ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            exit={reduced ? { opacity: 1 } : { opacity: 0, y: -12 }} transition={{ duration: reduced ? 0 : 0.35, ease: "easeOut" }}
            aria-live="polite" data-testid={`featured-subjects-spread-${slugTestId(s.subject)}`}>
            <Spread s={s} match={match} />
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
